/**
 * Balance Service
 * Handles balance inquiry API calls
 */

import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/config/api.config';
import type { BalanceResponse, AuditPremiumBalanceApiResponse, PaymentType } from '@/types/api.types';

/**
 * Transform Audit Premium API response to unified BalanceResponse format
 */
const transformAuditPremiumResponse = (data: AuditPremiumBalanceApiResponse): BalanceResponse => {
  return {
    policyNumber: data.policyNumber,
    insuredName: data.insuredName,
    insuredAddress: data.insuredAddress,
    insuredCityStateZip: data.insuredCityStateZip,
    currentDue: data.balanceDue,
    pastDue: data.pastDueAmount,
    totalBalance: data.balanceDue,
    feesAmount: data.feesAmount,
    paidAmount: data.paidAmount,
    isAuditPremium: true,
    transactions: [], // Audit Premium doesn't have transactions
    rsuiContact: data.rsuiContact,
  };
};

/**
 * Get balance information for a policy
 * Policy number is retrieved from the authenticated JWT token
 */
export const getBalance = async (paymentType: PaymentType, signal?: AbortSignal): Promise<BalanceResponse> => {
  if (paymentType === 'Deductible') {
    const response = await apiClient.get<BalanceResponse>(API_ENDPOINTS.BALANCE.GET_DEDUCTIBLE, { signal });
    return response.data;
  }

  // Audit Premium - transform the response to match BalanceResponse
  const response = await apiClient.get<AuditPremiumBalanceApiResponse>(API_ENDPOINTS.BALANCE.GET_AUDIT, { signal });
  return transformAuditPremiumResponse(response.data);
};
